import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Prism from 'prismjs';
import 'prismjs/themes/prism-tomorrow.css';
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-lua';
import 'prismjs/components/prism-json';
import 'prismjs/components/prism-css';
import 'prismjs/components/prism-sql';
import { parseEmojis } from '../utils/emojiParser';
import './Documentation.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function Documentation() {
  const [scripts, setScripts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedScript, setSelectedScript] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [expandedScripts, setExpandedScripts] = useState(new Set());
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [copiedCode, setCopiedCode] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [scriptsRes, categoriesRes] = await Promise.all([
        axios.get(`${API_URL}/api/scripts`),
        axios.get(`${API_URL}/api/documentation/categories`)
      ]);
      const scriptsData = scriptsRes.data;
      const categoriesData = categoriesRes.data;

      setScripts(scriptsData);
      setCategories(categoriesData);

      if (scriptsData.length > 0) {
        const firstScript = scriptsData[0];
        const firstCats = categoriesData
          .filter(c => c.script_id === firstScript.id && c.parent_id === null)
          .sort((a, b) => a.order_index - b.order_index);
        setSelectedScript(firstScript);
        setExpandedScripts(new Set([firstScript.id]));
        if (firstCats.length > 0) {
          setSelectedCategory(firstCats[0]);
        }
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const buildCategoryTree = (scriptId, parentId = null) => {
    return categories
      .filter(c => c.script_id === scriptId && c.parent_id === parentId)
      .sort((a, b) => a.order_index - b.order_index)
      .map(c => ({ ...c, children: buildCategoryTree(scriptId, c.id) }));
  };

  const findFirstWithContent = (tree) => {
    for (const cat of tree) {
      if (cat.content && cat.content.trim()) return cat;
      if (cat.children && cat.children.length > 0) {
        const found = findFirstWithContent(cat.children);
        if (found) return found;
      }
    }
    return null;
  };

  const toggleScript = (script) => {
    setSelectedScript(script);
    const newExp = new Set(expandedScripts);
    if (newExp.has(script.id)) {
      newExp.delete(script.id);
      setSelectedCategory(null);
    } else {
      newExp.add(script.id);
      const tree = buildCategoryTree(script.id);
      const firstCat = findFirstWithContent(tree);
      setSelectedCategory(firstCat || null);
    }
    setExpandedScripts(newExp);
  };

  const renderSidebarCategory = (category, level = 0) => {
    const hasChildren = category.children && category.children.length > 0;
    const hasContent = category.content && category.content.trim().length > 0;
    const isActive = selectedCategory?.id === category.id;
    const isExpanded = expandedCategories.has(category.id);

    return (
      <div key={category.id}>
        <button
          className={`sidebar-cat${isActive ? ' active' : ''}`}
          style={{ paddingLeft: `${14 + level * 14}px` }}
          onClick={() => {
            if (hasChildren) {
              const newExp = new Set(expandedCategories);
              if (newExp.has(category.id)) newExp.delete(category.id);
              else newExp.add(category.id);
              setExpandedCategories(newExp);
            }
            if (hasContent) setSelectedCategory(category);
          }}
        >
          <span className="sidebar-cat-name">{category.name}</span>
          {hasChildren && (
            <span className={`sidebar-chevron${isExpanded ? ' open' : ''}`}>›</span>
          )}
        </button>
        {hasChildren && isExpanded && (
          <div className="sidebar-cat-children">
            {category.children.map(child => renderSidebarCategory(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedCode(id);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const renderContent = (content, isCode) => {
    const parsedContent = parseEmojis(content);
    const codeId = `code-${selectedCategory?.id}`;

    if (isCode) {
      const language = 'javascript';
      const highlighted = Prism.highlight(parsedContent, Prism.languages[language], language);
      return (
        <div className="code-block-wrapper">
          <button
            className={`copy-button${copiedCode === codeId ? ' copied' : ''}`}
            onClick={() => copyToClipboard(parsedContent, codeId)}
          >
            {copiedCode === codeId ? '✓ Copied' : 'Copy'}
          </button>
          <pre className="code-block">
            <code className={`language-${language}`} dangerouslySetInnerHTML={{ __html: highlighted }} />
          </pre>
        </div>
      );
    }

    return (
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          code({ node, inline, className, children, ...props }) {
            const match = /language-(\w+)/.exec(className || '');
            const codeContent = String(children).replace(/\n$/, '');
            const mdCodeId = `md-${codeContent.slice(0, 30)}`;
            if (!inline && match) {
              const lang = match[1];
              const highlighted = Prism.highlight(
                codeContent,
                Prism.languages[lang] || Prism.languages.javascript,
                lang
              );
              return (
                <div className="code-block-wrapper">
                  <button
                    className={`copy-button${copiedCode === mdCodeId ? ' copied' : ''}`}
                    onClick={() => copyToClipboard(codeContent, mdCodeId)}
                  >
                    {copiedCode === mdCodeId ? '✓ Copied' : 'Copy'}
                  </button>
                  <pre className="code-block">
                    <code className={className} dangerouslySetInnerHTML={{ __html: highlighted }} {...props} />
                  </pre>
                </div>
              );
            }
            return <code className={className} {...props}>{children}</code>;
          }
        }}
      >
        {parsedContent}
      </ReactMarkdown>
    );
  };

  return (
    <div className="doc-page">
      {/* Sidebar */}
      <aside className="doc-sidebar">
        <div className="sidebar-inner">
          <div className="sidebar-label">Resources</div>
          {loading ? (
            <div className="sidebar-loading">Loading...</div>
          ) : (
            scripts.map(script => {
              const scriptCats = buildCategoryTree(script.id);
              if (scriptCats.length === 0) return null;
              const isExpanded = expandedScripts.has(script.id);
              const isActive = selectedScript?.id === script.id;
              return (
                <div key={script.id} className="sidebar-script-block">
                  <button
                    className={`sidebar-script${isActive ? ' script-active' : ''}`}
                    onClick={() => toggleScript(script)}
                  >
                    <span>{script.title}</span>
                    <span className={`sidebar-chevron${isExpanded ? ' open' : ''}`}>›</span>
                  </button>
                  {isExpanded && (
                    <div className="sidebar-script-cats">
                      {scriptCats.map(cat => renderSidebarCategory(cat))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className="doc-main">
        {loading ? (
          <div className="doc-welcome"><p>Loading documentation...</p></div>
        ) : !selectedScript ? (
          <div className="doc-welcome">
            <h1>Documentation</h1>
            <p>Select a script from the sidebar to view its documentation.</p>
          </div>
        ) : selectedCategory ? (
          <div className="doc-article fade-in">
            <div className="doc-breadcrumb-bar">
              <span className="doc-breadcrumb-label">Resources</span>
              <span className="doc-breadcrumb-sep">›</span>
              <span
                className="doc-breadcrumb-link"
                onClick={() => setSelectedCategory(null)}
              >
                {selectedScript.title}
              </span>
            </div>
            <h1 className="doc-article-title">{selectedCategory.name}</h1>
            <div className="doc-body">
              {renderContent(selectedCategory.content, selectedCategory.is_code)}
            </div>
          </div>
        ) : (
          <div className="doc-article fade-in">
            <div className="doc-breadcrumb-bar">
              <span className="doc-breadcrumb-label">Resources</span>
            </div>
            <h1 className="doc-article-title">{selectedScript.title}</h1>
            {selectedScript.description ? (
              <div className="doc-body">
                {renderContent(selectedScript.description, false)}
              </div>
            ) : (
              <p className="doc-script-hint">Select a category from the sidebar to view its documentation.</p>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

export default Documentation;
